import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColumn } from 'src/components/GridWrapper';
import AmountCell from 'src/sections/view_budget_period/custom_cells/AmountCell';
import AmountFooterCell from 'src/sections/view_budget_period/custom_cells/AmountFooterCell';
import AmountSubCell from 'src/sections/view_budget_period/custom_cells/AmountSubCell';
import BudgetPeriodCell from 'src/sections/view_budget_period/custom_cells/BudgetPeriodCell';
import BudgetPeriodFooterCell from 'src/sections/view_budget_period/custom_cells/BudgetPeriodFooterCell';
import DeleteActionCell from 'src/sections/view_budget_period/custom_cells/DeleteActionCell';
import PercentageCell from 'src/sections/view_budget_period/custom_cells/PercentageCell';
import PercentageFooterCell from 'src/sections/view_budget_period/custom_cells/PercentageFooterCell';
import PercentageSubCell from 'src/sections/view_budget_period/custom_cells/PercentageSubCell';
import WarningCell from 'src/sections/view_budget_period/custom_cells/WarningCell';
import WarningFooterCell from 'src/sections/view_budget_period/custom_cells/WarningFooterCell';
import { getScrollbarWidth } from 'src/utils/getScrollBarWidth';
import ExpandedCell from '../../../sections/view_budget_period/custom_cells/ExpandedCell';
import ExpandedHeaderCell from '../../../sections/view_budget_period/custom_cells/ExpandedHeaderCell';

const specificStyle = (field: string, value: any) => {
  if (Number(value[field]) < 0) {
    return {
      color: '#D9534F',
      backgroundColor: 'rgba(217, 170, 79, 0.2)',
    };
  }
  return {};
};

export const useBudgetPeriodColumn = (isReadOnly: boolean): GridColumn<any>[] => {
  const { t } = useTranslation();

  const ViewBudgetPeriodColumns = useMemo(
    () =>
      [
        {
          field: 'expanded',
          title: '',
          menu: false,
          width: 40,
          component: ExpandedCell,
          headerCell: ExpandedHeaderCell,
        },
        {
          field: 'errors',
          title: '',
          menu: false,
          component: WarningCell,
          footerCell: WarningFooterCell,
          width: 40,
        },
        {
          field: 'glPeriodId',
          title: t('Accounting Period'),
          dataType: 'string',
          component: BudgetPeriodCell,
          footerCell: BudgetPeriodFooterCell,
          menu: false,
        },
        {
          field: 'percentageAmount',
          title: `% ${t('Distribution')}`,
          format: '{0:n2}',
          dataType: 'fixed',
          component: PercentageCell,
          footerCell: PercentageFooterCell,
          menu: false,
        },
        {
          field: 'amount',
          title: `$ ${t('Distribution')}`,
          dataType: 'fixed',
          format: '{0:c2}',
          component: AmountCell,
          footerCell: AmountFooterCell,
          menu: false,
        },
        ...(isReadOnly
          ? []
          : [
              {
                field: '',
                title: t('Actions'),
                component: DeleteActionCell,
                menu: false,
                width: 100,
              },
            ]),
      ] as GridColumn<any>[],
    [isReadOnly, t]
  );

  return ViewBudgetPeriodColumns;
};

export const useBudgetPeriodSubColumn = (): GridColumn<any>[] => {
  const { t } = useTranslation();
  const scrollWidth = getScrollbarWidth();

  const ViewBudgetSubColumns = [
    {
      field: 'glCodeContent',
      title: t('GL Code'),
      dataType: 'string',
      menu: false,
    },
    {
      field: 'beginBalanceAmount',
      title: t('Beginning'),
      dataType: 'dollar',
      format: '{0:c2}',
      specificStyle,
      menu: false,
    },
    {
      field: 'endingBalanceAmount',
      title: t('Ending'),
      dataType: 'dollar',
      format: '{0:c2}',
      specificStyle,
      menu: false,
    },
    {
      field: 'distributionPercentage',
      title: `% ${t('Distribution')}`,
      dataType: 'fixed',
      component: PercentageSubCell,
      format: '{0:n2}',
      menu: false,
    },
    {
      field: 'distributionAmount',
      title: `$ ${t('Distribution')}`,
      dataType: 'dollar',
      component: AmountSubCell,
      format: '{0:c2}',
      menu: false,
      width: 100 - scrollWidth,
    },
  ] as GridColumn<any>[];

  return ViewBudgetSubColumns;
};
